import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { AlertDialog, Button, DialogContainer, Flex, ProgressCircle, Text, View } from '@adobe/react-spectrum';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { GetActivityRequest } from '../../../../services/soap/project/requests/GetActivityRequest';
import { GetProjectRequest } from '../../../../services/soap/project/requests/GetProjectRequest';
import { GetFolderRequest } from '../../../../services/soap/project/requests/GetFolderRequest';
import { ListFormsRequest } from '../../../../services/soap/form/requests/ListFormsRequest';
import { Component, FormDefinition } from './components/FormDefinitions';
import { SoapUtils } from '../../../../utils/SoapUtils';
import { FormControl } from './components/FormControl';
import { ListItemValueRequest } from '../../../../services/soap/form/requests/ListItemValueRequest';
import { Rows } from '../../../../services/soap/form/responses/ListItemValueResponse';
import { ItemValue, UpdateItemValueRequest } from '../../../../services/soap/form/requests/UpdateItemValueRequest';
import { ToastQueue } from '@react-spectrum/toast';
import { GetFolderProjectTypeRequest } from '../../../../services/soap/project/requests/GetFolderProjectTypeRequest';
import { useTranslation } from 'react-i18next';
import { AtollonEvent as FormAtollonEvent } from '../../../../infra/events/AtollonEvent';
import { AtollonEvent as AppEvent, publish } from '../../../../infra/events/Events';
import { usePreloadAssets } from '../../../../hooks/UsePreloadAssets';
import { ListCurrencyRateRequest } from '../../../../services/soap/finance/requests/ListCurrencyRateRequest';
import { GetItemRequest } from '../../../../services/soap/item/requests/GetItemRequest';
import { UNSAFE_NavigationContext } from 'react-router-dom';

export interface IFormViewProps {
    formIdType?: string;
    formId?: string;
    formIdMode?: string;
    isSaveEnabled?: boolean;
    isSaveOnRightSide?: boolean;
    selectedItem?: FPAData;
    isNew?: boolean;
    setFormViewData?: (key: string, value: any) => void;
    getFormViewData?: (key: string, default_value: any) => any;
}

function FormView ({
    formIdType,
    formId,
    formIdMode,
    isSaveEnabled,
    isSaveOnRightSide,
    selectedItem,
    isNew,
    setFormViewData,
    getFormViewData
}: IFormViewProps) {
  // const selectedItem = useSelector((state: RootState) => state.finder.selectedItem);

  const { 
    formService,
    projectService, 
    financeService,
    itemService,
    store 
  } = useDependency();

  const [formDefinition, setFormDefinition] = useState<FormDefinition|null>(null);
  const [formData, setFormData] = useState<Map<string, any>>(new Map<string, any>());
  const formDataRef = useRef<Map<string, any>>(new Map<string, any>());
  const realFormId = useRef('0');
  const requiredFields = useRef<string[]>([]);
  const formAtollonIdToFormItemId = useRef<Map<string, string>>(new Map<string, string>());
  const scriptTimerRef = useRef<NodeJS.Timer | null>(null);
  const { t } = useTranslation();
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const { currencies } = usePreloadAssets();
  const currencyRates = useRef<Map<string, any>>(new Map<string, any>());

  const _t = (key: string) => t( `formView.${key}`, { ns: 'layout-components' });

  const baselineValuesRef = useRef<Map<string, string>>(new Map());
  const focusBaselineRef = useRef<Map<string, string>>(new Map());
  const pendingSavesRef = useRef<Set<string>>(new Set());
  const saveChainRef = useRef<Map<string, Promise<void>>>(new Map());
  const [pendingCount, setPendingCount] = useState(0);
  const [isManualSaving, setIsManualSaving] = useState(false);
  const [baselineTick, setBaselineTick] = useState(0);

  const normalizeValue = useCallback((value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }, []);

  const getCurrentNormalizedValue = useCallback((key: string): string => {
    // Prefer getFormViewData in isNew mode (formData may not be populated yet)
    if (isNew && getFormViewData) {
      return normalizeValue(getFormViewData(key, ''));
    }
    const item = formDataRef.current.get(key);
    return normalizeValue(item?.value ?? '');
  }, [getFormViewData, isNew, normalizeValue]);

  const setKeyPending = useCallback((key: string, isPending: boolean) => {
    const k = String(key);
    if (isPending) pendingSavesRef.current.add(k);
    else pendingSavesRef.current.delete(k);
    setPendingCount(pendingSavesRef.current.size);
  }, []);

  const enqueueFieldSave = useCallback((key: string, job: () => Promise<void>) => {
    const k = String(key);
    const prev = saveChainRef.current.get(k) ?? Promise.resolve();
    const next = prev
      .catch(() => undefined)
      .then(job)
      .catch(() => undefined);
    saveChainRef.current.set(k, next);
    return next;
  }, []);

  const saveField = useCallback(async (key: string) => {
    if (isNew) return; // Auto-save disabled for new forms

    const k = String(key);
    const currentNormalized = getCurrentNormalizedValue(k);
    const lastSavedNormalized = baselineValuesRef.current.get(k) ?? '';
    if (currentNormalized === lastSavedNormalized) return;

    if (!realFormId.current || realFormId.current === '0') return;

    await enqueueFieldSave(k, async () => {
      const currentItem = formDataRef.current.get(k);
      const itemValue = currentItem
        ? new ItemValue(
          currentItem.id,
          currentItem.acl,
          currentItem.formInfo ?? realFormId.current,
          currentItem.formItem ?? k,
          currentItem.formItemName,
          currentItem.value,
          currentItem.typ,
          currentItem.context
        )
        : new ItemValue(undefined, undefined, realFormId.current as any, k as any, undefined, currentNormalized, undefined, undefined);

      setKeyPending(k, true);
      try {
        const result = await formService.updateItemValue(
          new UpdateItemValueRequest(store.Server, store.SessionId, [itemValue])
        );
        if (result.result === 'OK') {
          baselineValuesRef.current.set(k, currentNormalized);
          focusBaselineRef.current.set(k, currentNormalized);
          setBaselineTick((x) => x + 1);
        } else {
          ToastQueue.negative('Error saving field', { timeout: 3000 });
        }
      } catch (e: any) {
        ToastQueue.negative(e?.message ?? 'Error saving field', { timeout: 3000 });
      } finally {
        setKeyPending(k, false);
      }
    });
  }, [enqueueFieldSave, formService, getCurrentNormalizedValue, isNew, setKeyPending, store.Server, store.SessionId]);

  const handleFieldFocus = useCallback((key: string) => {
    const k = String(key);
    const normalized = getCurrentNormalizedValue(k);
    focusBaselineRef.current.set(k, normalized);

    // For isNew mode we may not have a server baseline; treat first focus as initial baseline.
    if (!baselineValuesRef.current.has(k)) {
      baselineValuesRef.current.set(k, normalized);
      setBaselineTick((x) => x + 1);
    }
  }, [getCurrentNormalizedValue]);

  const handleFieldBlur = useCallback((key: string) => {
    const k = String(key);
    const currentNormalized = getCurrentNormalizedValue(k);
    const focusBaseline = focusBaselineRef.current.get(k);
    const baseline = focusBaseline ?? baselineValuesRef.current.get(k) ?? '';
    if (currentNormalized === baseline) return;
    void saveField(k);
  }, [getCurrentNormalizedValue, saveField]);

  const isDirty = useMemo(() => {
    // ensure baseline updates trigger recalculation
    void baselineTick;

    const currentNormalizedByKey = new Map<string, string>();
    formData.forEach((value: any, key: any) => {
      currentNormalizedByKey.set(String(key), normalizeValue(value?.value ?? ''));
    });

    const keys = new Set<string>();
    baselineValuesRef.current.forEach((_v, key) => keys.add(String(key)));
    currentNormalizedByKey.forEach((_v, key) => keys.add(String(key)));

    for (const key of keys) {
      const baseline = baselineValuesRef.current.get(key) ?? '';
      const current = currentNormalizedByKey.get(key) ?? '';
      if (baseline !== current) return true;
    }
    return false;
  }, [baselineTick, formData, normalizeValue]);

  const totalPending = pendingCount + (isManualSaving ? 1 : 0);
  const shouldWarnOnLeave = isDirty || totalPending > 0;

  const self = useMemo<FPAData | undefined | null>(() => {
    let tmp: FPAData | undefined | null = null;
    switch(formIdType) {
      case 'self':
        tmp = selectedItem;
        break;
      case 'project':
        tmp = selectedItem;
        while(tmp && tmp.type !== FPADataTypes.PROJECT) {
          tmp = tmp.parent;
        }
        break;
      case 'folder':
        tmp = selectedItem;
        while(tmp && tmp.type !== FPADataTypes.FOLDER) {
          tmp = tmp.parent;
        }
        break;
    }
    return tmp;
  }, [formIdType, selectedItem]);

  const generateIdToFormItemId = useCallback((components: Component[]) => {
    components.forEach((component: Component) => {
      if(component.PROPERTIES.id && component.FORMITEM) {
        formAtollonIdToFormItemId.current.set(component.PROPERTIES.id, component.FORMITEM);
        formAtollonIdToFormItemId.current.set(component.FORMITEM, component.PROPERTIES.id);
      }

      if(component.CHILDREN) {
        generateIdToFormItemId(component.CHILDREN.COMPONENT);
      }
    });
  }, []);

  const handleSaveForm = async () => {
      setIsManualSaving(true);
      const valuesArray = Array.from(formData.values());

      const passed_values = valuesArray.map((value: any) => {
        return new ItemValue( value.id, value.acl, value.formInfo, value.formItem, value.formItemName, value.value, value.typ, value.context);
      });

      var is_required_fields_missing = requiredFields.current.reduce((acc: boolean, field: string) => {
        if(acc) {
          return true;
        }
        if(!formData.has(field) || !formData.get(field).value) {
          return true;
        }
        return acc;
      }, false);

      if(is_required_fields_missing) {
        ToastQueue.negative('Required fields are missing', { timeout: 3000 });
        setIsManualSaving(false);
        return;
      }

      try {
        var save_response = await formService.updateItemValue(new UpdateItemValueRequest(store.Server, store.SessionId, passed_values));
        if(save_response.result === 'OK') {
          // After a successful full save, update baseline to current values
          const nextBaseline = new Map<string, string>();
          formData.forEach((value: any, key: any) => {
            nextBaseline.set(String(key), normalizeValue(value?.value ?? ''));
          });
          baselineValuesRef.current = nextBaseline;
          setBaselineTick((x) => x + 1);
          ToastQueue.positive('Form saved successfully', { timeout: 3000 });
        } else {
          ToastQueue.negative('Error saving form', { timeout: 3000 });
        }
      } finally {
        setIsManualSaving(false);
      }
  };

  const loadFormCallback = useCallback(async () => {
    setShowLoader(true);
    try{
      if(self === null || self === undefined) return;
      if(self.type === FPADataTypes.FOLDER_TYPE || self.type === FPADataTypes.ITEM_TYPE) return;
      
      var form_id = null; 
      var formValuesId = null;

      if(isNew) {
        form_id = formId;
      } else {
        switch(self.type) {
            case FPADataTypes.ACTIVITY:
              var activity = (await projectService.getActivity(new GetActivityRequest(store.Server, store.SessionId, self.id))).ACTIVITY;
              form_id = formIdMode === 'template' ? activity.formId : formId;
              formValuesId = activity.formValues;
              break;
            case FPADataTypes.PROJECT:
              var project = (await projectService.getProject(new GetProjectRequest(store.Server, store.SessionId, self.id))).PROJECT;
              form_id = formIdMode === 'template' ? project.formId : formId;
              formValuesId = project.formValues;
              break;
            case FPADataTypes.FOLDER:
              var folder = (await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, self.id))).FOLDER;
              form_id = formIdMode === 'template' ? folder.formId : formId;
              formValuesId = folder.formValues;
              break;
        }
      }
  
      if(formIdMode === 'type'){
        var type_info = await projectService.getFolderProjectType(new GetFolderProjectTypeRequest(store.Server, store.SessionId, self.item_type_id));
        form_id = type_info.TYPE[0].formId;
      }
  
      if(!form_id) return;
  
      realFormId.current = formValuesId!;
      var form = await formService.listForms(new ListFormsRequest(store.Server, store.SessionId, form_id!));
  
      var jsonObject = SoapUtils.parseXmlString2(form.ROW.definition, ['COMPONENT']);
      setFormDefinition(jsonObject);
      
      // Add null safety checks before accessing nested properties
      if(jsonObject && jsonObject.definition && jsonObject.definition.gui && jsonObject.definition.gui.COMPONENT) {
        requiredFields.current = [];

        generateIdToFormItemId(jsonObject.definition.gui.COMPONENT);
      }
      
      if(jsonObject.definition && jsonObject.definition.scripts) {
        // eslint-disable-next-line no-eval
        eval(jsonObject.definition.scripts);
      }
      if(!isNew) {
        var formValues = await formService.listItemValue(new ListItemValueRequest(store.Server, store.SessionId, formValuesId!));
        
        if(formValues.count > 0) {
          var valuesMap = formValues.ROWS.reduce((acc: Map<string, any>, row: Rows) => { 
            acc.set(String(row.ITEMVALUE.formItem), row.ITEMVALUE);              
            return acc
          }, new Map<string, any>());
          setFormData(valuesMap);

          const nextBaseline = new Map<string, string>();
          valuesMap.forEach((item: any, key: any) => {
            nextBaseline.set(String(key), normalizeValue(item?.value ?? ''));
          });
          baselineValuesRef.current = nextBaseline;
          setBaselineTick((x) => x + 1);
        }
      } 
    } catch (e) {
      console.error(e);
    } finally {
      setShowLoader(false);
    }

  }, [formId, formIdMode, formService, generateIdToFormItemId, isNew, normalizeValue, projectService, self, store.Server, store.SessionId]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const updateData = useCallback((key: string, value: any) => {
    if(setFormViewData){
      setFormViewData(key, value);
    }
    setFormData(prevFormData => {
      const newMap = new Map(prevFormData);
      let modified_item = null;
      
      if(newMap.has(key)) {
        modified_item = {...newMap.get(key), value: value};
      } else {
        modified_item = {
          value: value,
          id: null,
          acl: '',
          formInfo: realFormId.current,
          formItem: key,
          formItemName: '',
          typ: '',
          context: ''
        }
      }
      
      newMap.set(key, modified_item);
      return newMap;
    });

    const formItemId = formAtollonIdToFormItemId.current.get(key);
    if(formItemId) {
      window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.FORM_VALUE_UPDATED, {componentId: formItemId, value: value}));
    }
  }, [setFormViewData]);

  const getData = (key: string, default_value: any) => {
    if(getFormViewData && isNew){
      return getFormViewData(key, default_value);
    }
    var item = formData.get(key);
    return formData.has(key) && item.value ? item.value : default_value;
  }

  useEffect(() => {
    loadFormCallback();
  }, [loadFormCallback]);

  const handleFormValueChanged = useCallback((event: FormAtollonEvent) => {
    console.log('FORM_VALUE_CHANGED', event.componentId, event.Value);
    if(event.componentId && formAtollonIdToFormItemId.current.has(event.componentId)) {
      updateData(formAtollonIdToFormItemId.current.get(event.componentId)!, event.Value);
    }
  }, [updateData]);

  const handleRequestData = useCallback(async (event: FormAtollonEvent) => {
    console.log('REQUEST_DATA', event.componentId, event.Value);
    if(!event.componentId){
      window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_ERROR, {componentId: event.componentId, value: event.Value, error: 'Component ID is required'}));
      return;
    }
    
    if(FormAtollonEvent.ALLOWED_REQUEST_DATA_COMPONENTS.includes(event.componentId)) {
      switch(event.componentId) {
        case 'currency':
          window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_RECEIVED, {componentId: event.componentId, value: event.Value}));
          break;
        case 'currencyRate':
          var currency = currencies.find((currency: any) => currency.code === event.Value.value);
          if(currency) {
            if(currencyRates.current.has(currency.id)) {
              window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_RECEIVED, {componentId: event.componentId, value: { rate: currencyRates.current.get(currency.id).rate, tag: event.Value.tag }}));
            } else {
              var currency_rate = await financeService.listCurrencyRate(new ListCurrencyRateRequest(store.Server, store.SessionId, {
                currency: currency.id,
                rateDate: new Date().toISOString().split('T')[0]
              }));
              console.log('currency_rate', currency_rate);
              if(currency_rate.EXCEPTION) {
                window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_ERROR, {componentId: event.componentId, value: event.Value, error: 'Currency rate not found'}));
              } else {
                currencyRates.current.set(currency.id, { rate: +currency_rate.currencyRate.rate, tag: event.Value.tag });
                window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_RECEIVED, {componentId: event.componentId, value: { rate: +currency_rate.currencyRate.rate, tag: event.Value.tag }}));
              }
            }
          } else {
            window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_ERROR, {componentId: event.componentId, value: event.Value, error: 'Currency not found'}));
          }
          break;
        case 'product':
          console.log('requesting item: ', event.Value.value);
          var product = await itemService.getItem(new GetItemRequest(store.Server, store.SessionId, event.Value.value));
          if(product.result === 'OK') {
            window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_RECEIVED, {componentId: event.componentId, value: product.ITEM}));
          } else {
            window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_ERROR, {componentId: event.componentId, value: event.Value, error: 'Product not found'}));
          } 
          break;
      }
    } else {
      window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.DATA_ERROR, {componentId: event.componentId, value: event.Value, error: 'Component ID is not allowed to request data'}));
    }
  }, [currencies, financeService, itemService, store.Server, store.SessionId]);

  useEffect(() => {
    window.addEventListener(FormAtollonEvent.FORM_VALUE_CHANGED, handleFormValueChanged as any);
    window.addEventListener(FormAtollonEvent.REQUEST_DATA, handleRequestData as any);

    if(!scriptTimerRef.current) {
      scriptTimerRef.current = setInterval(() => {
        if((window as any).removeScriptListeners) {
          console.log('sending component loaded event');
          window.dispatchEvent(new FormAtollonEvent(FormAtollonEvent.FORM_COMPONENT_LOADED, {componentId: 'currency', value: '1'}));
          clearInterval(scriptTimerRef.current!);
        }
      }, 100);
    }

    return () => {
      window.removeEventListener(FormAtollonEvent.FORM_VALUE_CHANGED, handleFormValueChanged as any);
      window.removeEventListener(FormAtollonEvent.REQUEST_DATA, handleRequestData as any);

      if((window as any).removeScriptListeners) {
        console.log('Removing script listeners');
        (window as any).removeScriptListeners();
      } else {
        console.warn('No removeScriptListeners function found');
      }
      (window as any).removeScriptListeners = null;
      if(scriptTimerRef.current) {
        clearInterval(scriptTimerRef.current);
        scriptTimerRef.current = null;
      }
    }
  }, [currencies, handleFormValueChanged, handleRequestData]);

  // Browser-native guard for refresh/close
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!shouldWarnOnLeave) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [shouldWarnOnLeave]);

  // Publish guard state so Finder can block closeDetails / selection changes
  const contextId = self?.id;
  useEffect(() => {
    if (!contextId) return;
    publish(AppEvent.FORM_GUARD_CHANGED, { contextId, isDirty, pendingCount: totalPending });
    return () => {
      publish(AppEvent.FORM_GUARD_CHANGED, { contextId, isDirty: false, pendingCount: 0 });
    };
  }, [contextId, isDirty, totalPending]);

  // In-app navigation guard (tab switch / route nav)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingTx, setPendingTx] = useState<any>(null);
  const navigationContext = useContext(UNSAFE_NavigationContext) as any;

  useEffect(() => {
    if (!shouldWarnOnLeave) return;
    const navigator = navigationContext?.navigator;
    if (!navigator?.block) return;

    const unblock = navigator.block((tx: any) => {
      setPendingTx(tx);
      setShowLeaveDialog(true);
    });

    return unblock;
  }, [navigationContext, shouldWarnOnLeave]);

  const markMandatory = (key: string) => {
    if(!requiredFields.current.includes(key)) {
      requiredFields.current.push(key);
    }
  }

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  }
  
  // if(formDefinition === null)
  //   return (
  //     <>
  //       <IllustratedMessage>
  //         <Unavailable />
  //         <Heading>{_t('missingDefinition.title')}</Heading>
  //         <Content>
  //           {_t('missingDefinition.message')}
  //         </Content>

  //       </IllustratedMessage>
  //     </>
  //   );
  // else 
  if (formDefinition === null || !formDefinition.definition || !formDefinition.definition.gui || !formDefinition.definition.gui.COMPONENT)
    return (
      <>
       
      </>
    );
  else
    return (
      <>
      <DialogContainer onDismiss={() => {
        if (showLeaveDialog) {
          setShowLeaveDialog(false);
          setPendingTx(null);
        }
      }}>
        {showLeaveDialog && (
          <AlertDialog
            variant="confirmation"
            title="Unsaved changes"
            primaryActionLabel="Leave"
            cancelLabel="Stay"
            onPrimaryAction={() => {
              setShowLeaveDialog(false);
              const tx = pendingTx;
              setPendingTx(null);
              if (tx?.retry) tx.retry();
            }}
            onCancel={() => {
              setShowLeaveDialog(false);
              setPendingTx(null);
            }}
          >
            {totalPending > 0
              ? 'Saving in progress. You have unsaved changes. Are you sure you want to leave?'
              : 'You have unsaved changes. Are you sure you want to leave?'}
          </AlertDialog>
        )}
      </DialogContainer>
      <Flex direction={'column'} gap={10}>
        {/* <View UNSAFE_className='tblContainer'> */}
          <FormControl
            component={formDefinition.definition.gui.COMPONENT[0]}
            getData={getData}
            updateData={updateData}
            markMandatory={markMandatory}
            onFieldFocus={handleFieldFocus}
            onFieldBlur={handleFieldBlur}
          />
        {/* </View> */}
        {isSaveEnabled && (<View alignSelf={isSaveOnRightSide ? 'end' : 'start'}>
          <Flex direction="row" gap="size-200" alignItems="center">
            {totalPending > 0 && (
              <Flex direction="row" gap="size-100" alignItems="center">
                <ProgressCircle aria-label="Saving…" isIndeterminate size="S" />
                <Text>Saving…</Text>
              </Flex>
            )}
            <Button
              variant="cta"
              onPress={() => handleSaveForm()}
              isDisabled={!isDirty || totalPending > 0}
            >
              {isDirty ? `${_t('button.save')}*` : _t('button.save')}
            </Button>
          </Flex>
        </View>)}
      </Flex>
      </>
    );
}

export const FormViewComponent = LayoutComponent(FormView);